import { createColumnHelper } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { TractNRI } from 'components/nri-table/types';
import {
  InterestCell,
  OverAllocationWarning,
} from 'components/nri-table/over-allocation-warning';
import { formatNumber } from 'utils/number';
import { tagsTextColumn } from 'lib/asset-aliases';
import { useKeyedRows } from './hooks';
import {
  type DataTableRow,
  ACRES_PRECISION,
  NRI_PRECISION,
  STATIC_TABLE_CONFIG,
  renderLink,
  slotUnitColumns,
} from './utils';

interface SlotAllocationsTableProps {
  rows: TractNRI[];
  isLoading: boolean;
}

type SlotAllocationsTableRow = DataTableRow<TractNRI>;

/**
 * Composite row-key strategy — prevents collisions when multiple rows
 * share the same `slotId` (this table aggregates per-tract per-slot).
 * Module scope so `useKeyedRows`'s memo stays warm.
 *
 * @param {TractNRI} row - Source row
 * @param {number} idx - Position in the array
 * @return {string} Stable composite row key
 */
const getRowKey = (row: TractNRI, idx: number): string =>
  `${row.slotId ?? 'row'}-${idx}`;

/**
 * Renders a slot-level interest with an over-100% warning beside it. These
 * columns would otherwise rely on DataTable's `meta.type` formatting, which has
 * no way to flag the value (INS-11621).
 *
 * @param {number | null | undefined} value - The slot-level interest.
 * @param {'NRI' | 'WI'} measure - Which measure the column shows.
 * @return {JSX.Element} The formatted value, plus a warning when it exceeds 1.0.
 */
const renderSlotInterest = (
  value: number | null | undefined,
  measure: 'NRI' | 'WI'
) => (
  <InterestCell>
    {value !== null && value !== undefined
      ? formatNumber(value, NRI_PRECISION, NRI_PRECISION)
      : '—'}
    <OverAllocationWarning value={value} measure={measure} />
  </InterestCell>
);

const columnHelper = createColumnHelper<SlotAllocationsTableRow>();

// `accessor()` returns `AccessorKeyColumnDef<Row, FieldType>`, while
// `DataTableColumnDef<Row>` is invariant on the value generic
// (`unknown`). The trailing cast bridges that variance — required.
const COLUMNS = [
  columnHelper.display({
    id: 'crmTract',
    header: 'CRM Tract',
    cell: ({ row }) =>
      renderLink(
        row.original.crmTract,
        row.original.crmTractSalesforceId
          ? `/crm/tracts_and_subtracts/${row.original.crmTractSalesforceId}/edit`
          : undefined
      ),
  }),
  // The tract's tags as comma text, `--` with none (INS-13689).
  tagsTextColumn<SlotAllocationsTableRow>({
    accessorKey: 'crmTractTags',
    id: 'crmTractTags',
    header: 'Tract Tags',
  }),
  ...slotUnitColumns(columnHelper, 'Well ID'),
  columnHelper.accessor('totalSlotNri', {
    id: 'totalSlotNri',
    header: 'Total Slot NRI',
    meta: { type: 'decimal', format: { maxDecimalPlaces: NRI_PRECISION } },
    cell: ({ getValue }) => renderSlotInterest(getValue(), 'NRI'),
  }),
  columnHelper.accessor('totalSlotWi', {
    id: 'totalSlotWi',
    header: 'Total Slot WI',
    meta: { type: 'decimal', format: { maxDecimalPlaces: NRI_PRECISION } },
    cell: ({ getValue }) => renderSlotInterest(getValue(), 'WI'),
  }),
  columnHelper.accessor('allocationMethod', {
    id: 'allocationMethod',
    header: 'Allocation Method',
    meta: { type: 'text' },
  }),
  columnHelper.accessor('tractNetAcres', {
    id: 'tractNetAcres',
    header: 'Tract Net Acres',
    meta: { type: 'decimal', format: { maxDecimalPlaces: ACRES_PRECISION } },
  }),
  columnHelper.accessor('netAcreageUnits', {
    id: 'netAcreageUnits',
    header: 'Net Acreage Units',
    meta: { type: 'text' },
  }),
  columnHelper.accessor('tractGrossAcres', {
    id: 'tractGrossAcres',
    header: 'Tract Gross Acres',
    meta: { type: 'decimal', format: { maxDecimalPlaces: ACRES_PRECISION } },
  }),
  columnHelper.accessor('surveyArea', {
    id: 'surveyArea',
    header: 'Survey Area',
    meta: { type: 'text' },
  }),
] as DataTableColumnDef<SlotAllocationsTableRow>[];

/**
 * Static, read-only `<DataTable>` for the Summary step's "Slot
 * Allocations" section — replaces the legacy `<FullNRITable>`'s
 * expand / sort / filter / CSV-export functionality with a simple
 * presentation. Well ID and Unit cells render as Insights-page links
 * when href fields are present.
 *
 * @param {SlotAllocationsTableProps} props - The component props.
 * @return {JSX.Element} The slot allocations table.
 */
export const SlotAllocationsTable = ({
  rows,
  isLoading,
}: SlotAllocationsTableProps): JSX.Element => {
  const keyedRows = useKeyedRows(rows, getRowKey);
  return (
    <DataTable<SlotAllocationsTableRow>
      data={keyedRows}
      columns={COLUMNS}
      getRowId={(row) => row.__key}
      isLoading={isLoading}
      config={STATIC_TABLE_CONFIG}
    />
  );
};
